import { WarningModalComponent } from "./modals/warning-modal/warning-modal.component";
import { BrowserModule } from "@angular/platform-browser";
import { ErrorHandler, NgModule } from "@angular/core";
import { AppComponent } from "./app.component";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { HeaderComponent } from "./components/header/header.component";
import { NgSpinKitModule } from "ng-spin-kit";
import { SpinnerComponent } from "./components/spinner/spinner.component";
import { ProgramArticlesComponent } from "./pages/program-articles/program-articles.component";
import { RouterModule } from "@angular/router";
import { appRoutes } from "./routing/routes";
import { NotFoundComponent } from "./pages/not-found/not-found.component";
import { TranslateComponent } from "./pages/translate/translate.component";
import { MagicBoxComponent } from "./pages/magic-box/magic-box.component";
import { TranslationService } from "./services/translation.service";
import { ApiService } from "./services/api.service";
import { HttpClientModule } from "@angular/common/http";
import { PictureCropperComponent } from "./components/picture-cropper/picture-cropper.component";
import { ImageCropperModule } from "ng2-img-cropper";
import { NgxDatatableModule } from "@swimlane/ngx-datatable";
import { PictureService } from "./services/picture.service";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { RootModalComponent } from "./modals/root-modal/root-modal.component";
import { FiltersModalComponent } from "./modals/filters-modal/filters-modal.component";
import { DateFilterPickerComponent } from "./components/date-filter-picker/date-filter-picker.component";
import {
  Md2AutocompleteModule,
  Md2DataTableModule,
  MdNativeDateModule,
  Md2DatepickerModule,
  Md2DialogModule,
  Md2OptionModule,
  Md2SelectModule,
  Md2TabsModule,
  Md2TooltipModule,
} from "md2";
import { StoreModule } from "@ngrx/store";
import { filtersReducer } from "./store/reducers/filters.reducer";
import { StoreDevtoolsModule } from "@ngrx/store-devtools";
import { FocusDirective } from "./directives/focus.directive";
import { userReducer } from "./store/reducers/user.reducer";
import { LoginComponent } from "./pages/login/login.component";
import { AuthService } from "./services/auth.service";
import { UserActions } from "./store/actions/user.actions";
import { CanActivateViaAuthGuard } from "./routing/auth-guard";
import { CanActivateLogin } from "./routing/login-guard";
import { TranslationsListComponent } from "./pages/translate/translations-list/translations-list.component";
import { translationTypesReducer } from "./store/reducers/translation-types.reducer";
import { TranslationTypesActions } from "./store/actions/translation-types.actions";
import { TranslationsDashboardComponent } from "./pages/translate/translations-dashboard/translations-dashboard.component";
import { ChartsModule } from "ng2-charts";
import { UndefinedToStringPipe } from "./pipes/undefined-to-string.pipe";
import {
  MdAutocompleteModule,
  MdButtonModule,
  MdButtonToggleModule,
  MdCardModule,
  MdCheckboxModule,
  MdChipsModule,
  MdDatepickerModule,
  MdDialogModule,
  MdIconModule,
  MdInputModule,
  MdListModule,
  MdOptionModule,
  MdRadioModule,
  MdSelectModule,
  MdSliderModule,
  MdSlideToggleModule,
  MdSnackBarModule,
  MdTableModule,
  MdToolbarModule,
  MdTooltipModule,
} from "@angular/material";
import { AdminComponent } from "./pages/admin/admin.component";
import { ConfirmComponent } from "./modals/confirm/confirm.component";
import { TranslationsFormSaved } from "./routing/translations-form-saved";
import { CheckboxTableComponent } from "./components/checkbox-table/checkbox-table.component";
import { UserService } from "./services/user.service";
import { UserProfileService } from "./services/user-profile.service";
import { SuccessComponent } from "./modals/success/success.component";
import { ActionService } from "./services/action.service";
import { ComponentService } from "./services/component.service";
import { PrivilegeService } from "./services/privilege.service";
import { SearchByTermPipe } from "./pipes/search-by-term.pipe";
import { UserModalComponent } from "./modals/user-modal/user-modal.component";
import { usersReducer } from "./store/reducers/users.reducer";
import { UsersActions } from "./store/actions/users.actions";
import { userProfilesReducer } from "./store/reducers/user-profiles.reducer";
import { UserProfilesActions } from "./store/actions/userProfiles.actions";
import { ExcludeByListPipe } from "./pipes/exclude-by-list.pipe";
import { FilterChartsPipe } from "./pipes/filter-charts";
import { UserProfileModalComponent } from "./modals/user-profile-modal/user-profile-modal.component";
import { PrivilegeCheckboxComponent } from "./components/privilege-checkbox/privilege-checkbox.component";
import { CropperPictureModalComponent } from "./modals/cropper-picture-modal/cropper-picture-modal.component";
import { Angulartics2GoogleAnalytics, Angulartics2Module } from "angulartics2";
import { ProgramArticleService } from "./services/program-article.service";
import { ProgramArticleDetailsComponent } from "./components/program-article-details/program-article-details.component";
import { ProgramArticleFormComponent } from "./components/program-article-form/program-article-form.component";
import { LanguageService } from "./services/language.service";
import { OrderByPipe } from "./pipes/order-by.pipe";
import { FilterDropdownPipe } from "./pipes/filter-dropdown-pipe";
import { ListSelectorComponent } from "./components/list-selector/list-selector.component";
import { ReferentialService } from "./services/referential.service";
import { FiltersService } from "./services/filters.service";
import { ChannelService } from "./services/channel.service";
import { FiltersActions } from "./store/actions/filters.actions";
import { UndefinedStringPipe } from "./pipes/undefined-string.pipe";
import { MagicBoxSearchComponent } from "./pages/magic-box/magic-box-search/magic-box-search.component";
import { MagicBoxService } from "./services/magic-box.service";
import { ObjectToArrayPipe } from "./pipes/object-to-array.pipe";
import { TinymceEditorComponent } from "./components/tinymce-editor/tinymce-editor.component";
import { ValueFromEntityPipe } from "./pipes/value-from-entity.pipe";
import { programArticlesReducer } from "./store/reducers/program-articles.reducer";
import { ProgramArticlesActions } from "./store/actions/program-articles.actions";
import { UrlOrBlobPipe } from "./pipes/url-or-blob.pipe";
import { filtersMagicBoxReducer } from "./store/reducers/filters-magic-box.reducer";
import { FiltersMagicBoxActions } from "./store/actions/filters-magic-box.actions";
import { translationsReducer } from "./store/reducers/translations.reducer";
import { TranslationsActions } from "./store/actions/translations.actions";
import { MagicBoxRuleFormComponent } from "./modals/magic-box-rule-form/magic-box-rule-form.component";
import { AdminService } from "./services/admin.service";
import { ProxyComponent } from "./pages/proxy/proxy.component";
import { BooleanToStringPipe } from "./pipes/boolean-to-string.pipe";
import { rulesReducer } from "./store/reducers/rules.reducer";
import { RulesActions } from "./store/actions/rules.actions";
import { GetItemFromIdPipe } from "./pipes/get-item-from-id.pipe";
import { ToStringPipe } from "./pipes/to-string.pipe";
import { MagicBoxScheduleComponent } from "./modals/magic-box-schedule/magic-box-schedule.component";
import { ScheduleService } from "./services/schedule.service";
import { RuleDuplicateWarningComponent } from "./modals/rule-duplicate-warning/rule-duplicate-warning.component";
import { RulesService } from "./services/rules.service";
import { LoadingComponent } from "./modals/loading/loading.component";
import { MultilingualModalComponent } from "./modals/multilingual-modal/multilingual-modal.component";
import { FilterByDestinationPipe } from "./pipes/filter-by-destination.pipe";
import { ReplaysModalComponent } from "./modals/replays-modal/replays-modal.component";
import { TranslateClassificationModalComponent } from "./modals/translate-classification-modal/translate-classification-modal.component";
import { DashboardComponent } from "./pages/dashboard/dashboard.component";
import { ProgramArticleDashboardComponent } from "./components/program-article-dashboard/program-article-dashboard.component";
import { filtersProgramArticleDashboardReducer } from "./store/reducers/filters-program-article-dashboard.reducer";
import { FiltersProgramArticleDashboard } from "./store/actions/filters-program-article-dashboard.actions";
import { filtersTranslateDashboardReducer } from "./store/reducers/filters-translate-dashboard.reducer";
import { FiltersTranslateDashboard } from "./store/actions/filters-translate-dashboard.actions";
import { PictogramModalComponent } from "./modals/pictogram-modal/pictogram-modal.component";
import { DeleteGeneratedFieldsComponent } from "./modals/delete-generated-fields/delete-generated-fields.component";
import { MulticamsModalComponent } from "./modals/multicams-modal/multicams-modal.component";
import { SidenavComponent } from "./components/sidenav/sidenav.component";
import { SidenavService } from "./services/sidenav.service";
import { EventArticleComponent } from "./pages/event-article/event-article.component";
import { EventArticleService } from "./services/event-article.service";
import { EventArticlesActions } from "./store/actions/event-articles.actions";
import { BTSportComponent } from "./pages/bt-sport/bt-sport.component";
import { BTSportService } from "./services/bt-sport.service";
import { AdditionalPicturesModalComponent } from "./modals/additional-pictures-modal/additional-pictures-modal.component";
import { additionalPicturesReducer } from "./store/reducers/additional-pictures.reducer";
import { AdditionalPicturesActions } from "./store/actions/additional-pictures.actions";
import { EventAdditionalPicturesModalComponent } from "./modals/event-additional-pictures-modal/event-additional-pictures-modal.component";
import { eventAdditionalPicturesReducer } from "./store/reducers/event-additional-pictures.reducer";
import { EventAdditionalPicturesActions } from "./store/actions/event-additional-pictures.actions";
import { eventArticlesReducer } from "./store/reducers/event-articles.reducer";
import { pictureFormatsReducer } from "./store/reducers/picture-formats.reducer";
import { PictureFormatsActions } from "./store/actions/picture-formats.actions";
import { PictureSetService } from "./services/pictureSet.service";
import { SharedService } from "./services/shared.service";
import { MaxMultilingualModalComponent } from "./modals/max-multilingual-modal/max-multilingual-modal.component";
import { EventMaxMultilingualModalComponent } from "./modals/event-max-multilingual-modal/event-max-multilingual-modal.component";

export function provideErrorHandler() {
  return new ErrorHandler();
}

// tslint:disable-next-line:max-classes-per-file
@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    SpinnerComponent,
    ProgramArticlesComponent,
    EventArticleComponent,
    BTSportComponent,
    NotFoundComponent,
    TranslateComponent,
    MagicBoxComponent,
    PictureCropperComponent,
    RootModalComponent,
    FiltersModalComponent,
    DateFilterPickerComponent,
    FocusDirective,
    LoginComponent,
    TranslationsListComponent,
    TranslationsDashboardComponent,
    UndefinedToStringPipe,
    AdminComponent,
    ConfirmComponent,
    CheckboxTableComponent,
    SuccessComponent,
    DeleteGeneratedFieldsComponent,
    SearchByTermPipe,
    UserModalComponent,
    ExcludeByListPipe,
    UserProfileModalComponent,
    PrivilegeCheckboxComponent,
    CropperPictureModalComponent,
    ProgramArticleDetailsComponent,
    ProgramArticleFormComponent,
    OrderByPipe,
    FilterDropdownPipe,
    FilterChartsPipe,
    ListSelectorComponent,
    UndefinedStringPipe,
    MagicBoxSearchComponent,
    ObjectToArrayPipe,
    TinymceEditorComponent,
    ValueFromEntityPipe,
    UrlOrBlobPipe,
    MagicBoxRuleFormComponent,
    ProxyComponent,
    BooleanToStringPipe,
    GetItemFromIdPipe,
    ToStringPipe,
    MagicBoxScheduleComponent,
    RuleDuplicateWarningComponent,
    WarningModalComponent,
    LoadingComponent,
    MultilingualModalComponent,
    FilterByDestinationPipe,
    ReplaysModalComponent,
    MulticamsModalComponent,
    AdditionalPicturesModalComponent,
    EventAdditionalPicturesModalComponent,
    TranslateClassificationModalComponent,
    DashboardComponent,
    ProgramArticleDashboardComponent,
    PictogramModalComponent,
    SidenavComponent,
    MaxMultilingualModalComponent,
    EventMaxMultilingualModalComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    MdButtonModule,
    MdToolbarModule,
    NgSpinKitModule,
    MdIconModule,
    MdListModule,
    HttpClientModule,
    MdTableModule,
    RouterModule,
    NgxDatatableModule,
    FormsModule,
    MdSliderModule,
    MdDialogModule,
    MdButtonModule,
    MdDatepickerModule,
    MdTooltipModule,
    MdSelectModule,
    MdOptionModule,
    MdSlideToggleModule,
    Md2OptionModule,
    Md2SelectModule,
    Md2DialogModule,
    Md2DatepickerModule,
    MdButtonToggleModule,
    MdNativeDateModule,
    MdDatepickerModule,
    MdCardModule,
    ChartsModule,
    MdInputModule,
    MdCheckboxModule,
    MdChipsModule,
    Md2DataTableModule,
    MdAutocompleteModule,
    FormsModule,
    ReactiveFormsModule,
    Md2AutocompleteModule,
    MdSnackBarModule,
    Md2TabsModule,
    ImageCropperModule,
    MdNativeDateModule,
    MdRadioModule,
    Md2TooltipModule,
    RouterModule.forRoot(appRoutes, { initialNavigation: true }),
    StoreModule.forRoot({
      userState: userReducer,
      usersState: usersReducer,
      userProfilesState: userProfilesReducer,
      filtersState: filtersReducer,
      filtersProgramArticleDashboard: filtersProgramArticleDashboardReducer,
      filtersTranslateDashboard: filtersTranslateDashboardReducer,
      translationTypesState: translationTypesReducer,
      programArticlesState: programArticlesReducer,
      additionalPicturesState: additionalPicturesReducer,
      eventAdditionalPicturesState: eventAdditionalPicturesReducer,
      eventArticlesState: eventArticlesReducer,
      filtersMagicBoxState: filtersMagicBoxReducer,
      translationsState: translationsReducer,
      rulesState: rulesReducer,
      pictureFormatsState: pictureFormatsReducer,
    }),
    StoreDevtoolsModule.instrument({ maxAge: 5 }),
    Angulartics2Module.forRoot([Angulartics2GoogleAnalytics]),
  ],
  providers: [
    ApiService,
    TranslationService,
    PictureService,
    AuthService,
    UserActions,
    CanActivateViaAuthGuard,
    CanActivateLogin,
    EventArticlesActions,
    TranslationTypesActions,
    UndefinedToStringPipe,
    TranslationsFormSaved,
    UserService,
    UserProfileService,
    ActionService,
    ComponentService,
    PrivilegeService,
    SearchByTermPipe,
    UsersActions,
    UserProfilesActions,
    ExcludeByListPipe,
    ProgramArticleService,
    EventArticleService,
    BTSportService,
    LanguageService,
    OrderByPipe,
    FilterDropdownPipe,
    FilterChartsPipe,
    ReferentialService,
    FiltersService,
    ChannelService,
    PictureSetService,
    FiltersActions,
    FiltersProgramArticleDashboard,
    FiltersTranslateDashboard,
    MagicBoxService,
    ProgramArticlesActions,
    FiltersMagicBoxActions,
    TranslationsActions,
    AdditionalPicturesActions,
    EventAdditionalPicturesActions,
    PictureFormatsActions,
    AdminService,
    RulesActions,
    ScheduleService,
    RulesService,
    ObjectToArrayPipe,
    { provide: ErrorHandler, useFactory: provideErrorHandler },
    SidenavService,
    SharedService,
  ],
  entryComponents: [
    FiltersModalComponent,
    ConfirmComponent,
    SuccessComponent,
    UserModalComponent,
    UserProfileModalComponent,
    CropperPictureModalComponent,
    PictogramModalComponent,
    MagicBoxRuleFormComponent,
    MagicBoxScheduleComponent,
    RuleDuplicateWarningComponent,
    WarningModalComponent,
    LoadingComponent,
    MultilingualModalComponent,
    ReplaysModalComponent,
    MulticamsModalComponent,
    AdditionalPicturesModalComponent,
    EventAdditionalPicturesModalComponent,
    TranslateClassificationModalComponent,
    DeleteGeneratedFieldsComponent,
    MaxMultilingualModalComponent,
    EventMaxMultilingualModalComponent
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}



// WEBPACK FOOTER //
// ./src/app/app.module.ts